import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, GripVertical, Trash2, Copy, MoveUp, MoveDown } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { getIdToken } from "@/lib/auth";

interface Product {
  id: string;
  name: string;
  description: string;
}

interface QuestionOption {
  id?: string;
  text: string;
  value: string;
  order: number;
}

interface Question {
  id?: string;
  text: string;
  type: 'OPEN_TEXT' | 'MULTIPLE_CHOICE' | 'RATING' | 'YES_NO';
  required: boolean;
  order: number;
  options?: QuestionOption[];
  enableSentimentAnalysis?: boolean;
  enableKeywordExtraction?: boolean;
}

interface CustomForm {
  id?: string;
  name: string;
  productId: string;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  questions: Question[];
}

interface FormDesignerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  form?: CustomForm | null;
  mode: 'create' | 'edit';
}

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Get Firebase ID token
const getAuthToken = async () => {
  return getIdToken();
};

// Helper function to add auth headers
const getAuthHeaders = async () => {
  const token = await getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

const FormDesignerModal = ({ isOpen, onClose, onSave, form, mode }: FormDesignerModalProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [formData, setFormData] = useState<CustomForm>({
    name: '',
    productId: '',
    status: 'DRAFT',
    questions: [],
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Load products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/products`, { headers });
        
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        
        const data = await response.json();
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast({
          title: "Error",
          description: "Failed to load products",
          variant: "destructive",
        });
      }
    };

    if (isOpen) {
      fetchProducts();
    }
  }, [isOpen, toast]);

  // Initialize form data when editing
  useEffect(() => {
    if (form && mode === 'edit') {
      setFormData({
        id: form.id,
        name: form.name,
        productId: form.productId,
        status: form.status,
        questions: form.questions.map(q => ({
          id: q.id,
          text: q.text,
          type: q.type,
          required: q.isRequired,
          order: q.order,
          enableSentimentAnalysis: q.enableSentimentAnalysis || false,
          enableKeywordExtraction: q.enableKeywordExtraction || false,
          options: q.options || [],
        })),
      });
    } else {
      setFormData({
        name: '',
        productId: '',
        status: 'DRAFT',
        questions: [],
      });
    }
  }, [form, mode, isOpen]);

  const addQuestion = () => {
    const newQuestion: Question = {
      text: '',
      type: 'OPEN_TEXT',
      required: false,
      order: formData.questions.length + 1,
      options: [],
      enableSentimentAnalysis: false,
      enableKeywordExtraction: false,
    };
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }));
  };

  const updateQuestion = (index: number, field: keyof Question, value: any) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      ),
    }));
  };

  const removeQuestion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions
        .filter((_, i) => i !== index)
        .map((q, i) => ({ ...q, order: i + 1 })),
    }));
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === formData.questions.length - 1)
    ) {
      return;
    }

    setFormData(prev => {
      const newQuestions = [...prev.questions];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
      
      // Update order numbers
      newQuestions.forEach((q, i) => {
        q.order = i + 1;
      });

      return {
        ...prev,
        questions: newQuestions,
      };
    });
  };

  const addOption = (questionIndex: number) => {
    const question = formData.questions[questionIndex];
    if (!question.options) return;

    const newOption: QuestionOption = {
      text: '',
      value: '',
      order: question.options.length + 1,
    };

    updateQuestion(questionIndex, 'options', [...question.options, newOption]);
  };

  const updateOption = (questionIndex: number, optionIndex: number, field: keyof QuestionOption, value: string) => {
    const question = formData.questions[questionIndex];
    if (!question.options) return;

    const newOptions = question.options.map((opt, i) => 
      i === optionIndex ? { ...opt, [field]: value } : opt
    );

    updateQuestion(questionIndex, 'options', newOptions);
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const question = formData.questions[questionIndex];
    if (!question.options) return;

    const newOptions = question.options
      .filter((_, i) => i !== optionIndex)
      .map((opt, i) => ({ ...opt, order: i + 1 }));

    updateQuestion(questionIndex, 'options', newOptions);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Form name is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.productId) {
      toast({
        title: "Error",
        description: "Please select a product",
        variant: "destructive",
      });
      return;
    }

    if (formData.questions.length === 0) {
      toast({
        title: "Error",
        description: "At least one question is required",
        variant: "destructive",
      });
      return;
    }

    // Validate questions
    for (let i = 0; i < formData.questions.length; i++) {
      const question = formData.questions[i];
      if (!question.text.trim()) {
        toast({
          title: "Error",
          description: `Question ${i + 1} text is required`,
          variant: "destructive",
        });
        return;
      }

      if (question.type === 'MULTIPLE_CHOICE' && (!question.options || question.options.length === 0)) {
        toast({
          title: "Error",
          description: `Question ${i + 1} must have at least one option`,
          variant: "destructive",
        });
        return;
      }

      if (question.type === 'MULTIPLE_CHOICE' && question.options) {
        for (let j = 0; j < question.options.length; j++) {
          const option = question.options[j];
          if (!option.text.trim() || !option.value.trim()) {
            toast({
              title: "Error",
              description: `Question ${i + 1}, option ${j + 1} text and value are required`,
              variant: "destructive",
            });
            return;
          }
        }
      }
    }

    setLoading(true);

    try {
      const headers = await getAuthHeaders();
      const url = mode === 'edit' 
        ? `${API_BASE_URL}/custom-forms/${formData.id}`
        : `${API_BASE_URL}/custom-forms`;
      
      const method = mode === 'edit' ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({
          name: formData.name,
          productId: formData.productId,
          questions: formData.questions.map(q => ({
            text: q.text,
            type: q.type,
            required: q.required,
            enableSentimentAnalysis: q.enableSentimentAnalysis || false,
            enableKeywordExtraction: q.enableKeywordExtraction || false,
            options: q.type === 'MULTIPLE_CHOICE' ? q.options : undefined,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save form');
      }

      toast({
        title: "Success",
        description: `Form ${mode === 'edit' ? 'updated' : 'created'} successfully`,
      });

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving form:', error);
      toast({
        title: "Error",
        description: `Failed to ${mode === 'edit' ? 'update' : 'create'} form`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    const labels = {
      OPEN_TEXT: 'Open Text',
      MULTIPLE_CHOICE: 'Multiple Choice',
      RATING: 'Rating (1-5)',
      YES_NO: 'Yes/No',
    };
    return labels[type as keyof typeof labels] || type;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Edit Custom Form' : 'Create New Custom Form'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Form Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Form Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="form-name">Form Name *</Label>
                  <Input
                    id="form-name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter form name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="product-select">Product *</Label>
                  <Select
                    value={formData.productId}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, productId: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Questions */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Questions</CardTitle>
                <Button onClick={addQuestion} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Question
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.questions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No questions yet. Click "Add Question" to get started.
                </div>
              ) : (
                formData.questions.map((question, index) => (
                  <Card key={index} className="border-2">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                          <Badge variant="outline">Question {index + 1}</Badge>
                          <Badge variant="secondary">
                            {getQuestionTypeLabel(question.type)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveQuestion(index, 'up')}
                            disabled={index === 0}
                          >
                            <MoveUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveQuestion(index, 'down')}
                            disabled={index === formData.questions.length - 1}
                          >
                            <MoveDown className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeQuestion(index)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Question Text *</Label>
                        <Textarea
                          value={question.text}
                          onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                          placeholder="Enter your question"
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Question Type</Label>
                          <Select
                            value={question.type}
                            onValueChange={(value: any) => updateQuestion(index, 'type', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="OPEN_TEXT">Open Text</SelectItem>
                              <SelectItem value="MULTIPLE_CHOICE">Multiple Choice</SelectItem>
                              <SelectItem value="RATING">Rating (1-5)</SelectItem>
                              <SelectItem value="YES_NO">Yes/No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center space-x-2 pt-6">
                          <Checkbox
                            id={`required-${index}`}
                            checked={question.required}
                            onCheckedChange={(checked) => updateQuestion(index, 'required', checked)}
                          />
                          <Label htmlFor={`required-${index}`}>Required</Label>
                        </div>
                      </div>

                      {/* AI Analysis Options for Open Text Questions */}
                      {question.type === 'OPEN_TEXT' && (
                        <div className="space-y-3 pt-2">
                          <Label className="text-sm font-medium">AI Analysis Options</Label>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`sentiment-${index}`}
                                checked={question.enableSentimentAnalysis || false}
                                onCheckedChange={(checked) => updateQuestion(index, 'enableSentimentAnalysis', checked)}
                              />
                              <Label htmlFor={`sentiment-${index}`} className="text-sm">
                                Enable Sentiment Analysis
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`keywords-${index}`}
                                checked={question.enableKeywordExtraction || false}
                                onCheckedChange={(checked) => updateQuestion(index, 'enableKeywordExtraction', checked)}
                              />
                              <Label htmlFor={`keywords-${index}`} className="text-sm">
                                Enable Keyword Extraction
                              </Label>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Multiple Choice Options */}
                      {question.type === 'MULTIPLE_CHOICE' && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label>Options</Label>
                            <Button onClick={() => addOption(index)} size="sm" variant="outline">
                              <Plus className="h-3 w-3 mr-1" />
                              Add Option
                            </Button>
                          </div>
                          {(!question.options || question.options.length === 0) ? (
                            <div className="text-center py-4 text-muted-foreground text-sm">
                              No options yet. Add at least one option.
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {question.options.map((option, optionIndex) => (
                                <div key={optionIndex} className="flex items-center gap-2">
                                  <div className="flex-1 grid grid-cols-2 gap-2">
                                    <Input
                                      value={option.text}
                                      onChange={(e) => updateOption(index, optionIndex, 'text', e.target.value)}
                                      placeholder="Option text"
                                    />
                                    <Input
                                      value={option.value}
                                      onChange={(e) => updateOption(index, optionIndex, 'value', e.target.value)}
                                      placeholder="Option value"
                                    />
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeOption(index, optionIndex)}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Preview */}
                      <div className="pt-4 border-t">
                        <Label className="text-sm text-muted-foreground">Preview</Label>
                        <div className="mt-2 p-3 bg-muted rounded-md">
                          <div className="font-medium mb-2">{question.text || 'Question text'}</div>
                          {question.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                          <div className="mt-2 text-sm text-muted-foreground">
                            {question.type === 'OPEN_TEXT' && <div>Text input field</div>}
                            {question.type === 'MULTIPLE_CHOICE' && (
                              <div>
                                {question.options?.map((opt, i) => (
                                  <div key={i} className="flex items-center gap-2">
                                    <input type="radio" disabled />
                                    <span>{opt.text || 'Option text'}</span>
                                  </div>
                                )) || 'No options defined'}
                              </div>
                            )}
                            {question.type === 'RATING' && <div>Rating scale (1-5)</div>}
                            {question.type === 'YES_NO' && (
                              <div>
                                <div className="flex items-center gap-2">
                                  <input type="radio" disabled />
                                  <span>Yes</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input type="radio" disabled />
                                  <span>No</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading ? 'Saving...' : (mode === 'edit' ? 'Update Form' : 'Create Form')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormDesignerModal; 